import React, { useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import {
  useAddSession,
  useCreateAttendanceDay,
  useEditSession,
  useUpdateAttendanceDay,
} from "../../../api/queries/attendanceQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/apiError";
import { formatPlainDate, todayInManila } from "../../../utils/dateTime";
import { employeeName } from "../../../utils/text";
import { DAY_STATUSES, timeValue, toManilaIso } from "./attendanceFormat";

/**
 * 400 (validation, times not on that date) stays in the dialog as a list.
 * 403 (own record, locked DTR week) and 409/404 close with a toast.
 */
const useErrors = (onClose) => {
  const [errors, setErrors] = useState([]);
  const onError = (err) => {
    const status = getApiErrorStatus(err);
    const msg = err?.response?.data?.message;
    const list = Array.isArray(msg)
      ? msg
      : [getApiErrorMessage(err, "Could not save.")];
    if (status === 400) setErrors(list);
    else {
      toast.error(list.join(" "));
      onClose();
    }
  };
  return { errors, setErrors, onError };
};

const Errors = ({ errors }) =>
  errors.length ? (
    <div className="alert alert-danger fs-085 py-2" role="alert">
      {errors.length === 1 ? (
        errors[0]
      ) : (
        <ul className="mb-0 ps-3">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

const Footer = ({ onClose, busy, label, disabled, formId }) => (
  <>
    <button
      type="button"
      className="btn btn-outline-secondary profile-btn"
      onClick={onClose}
      disabled={busy}
    >
      Cancel
    </button>
    <button
      type="submit"
      form={formId}
      className="btn btn-primary profile-btn"
      disabled={busy || disabled}
    >
      {busy && (
        <span
          className="spinner-border spinner-border-sm"
          aria-hidden="true"
        ></span>
      )}
      {label}
    </button>
  </>
);

const TimeFields = ({
  checkIn,
  checkOut,
  setCheckIn,
  setCheckOut,
  outRequired,
}) => (
  <div className="row g-3">
    <div className="col-6">
      <label htmlFor="att-in" className="profile-label">
        Check-in <span className="text-danger">*</span>
      </label>
      <input
        id="att-in"
        type="time"
        className="form-control"
        value={checkIn}
        onChange={(e) => setCheckIn(e.target.value)}
        required
      />
    </div>
    <div className="col-6">
      <label htmlFor="att-out" className="profile-label">
        Check-out {outRequired && <span className="text-danger">*</span>}
      </label>
      <input
        id="att-out"
        type="time"
        className="form-control"
        value={checkOut}
        min={checkIn || undefined}
        onChange={(e) => setCheckOut(e.target.value)}
      />
      <div className="fs-070 text-slate-500 mt-1">
        Leave empty while still open.
      </div>
    </div>
  </div>
);

const RemarksField = ({ value, onChange, placeholder }) => (
  <div className="mt-3">
    <label htmlFor="att-remarks" className="profile-label">
      Remarks <span className="text-slate-400 fw-normal">(optional)</span>
    </label>
    <textarea
      id="att-remarks"
      className="form-control"
      rows={2}
      maxLength={500}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

/** Add a check-in period to a day, or edit one. Times are Manila time on that day. */
export const SessionDialog = ({ day, session, onClose }) => {
  const isEdit = !!session;
  const [checkIn, setCheckIn] = useState(timeValue(session?.checkInTime));
  const [checkOut, setCheckOut] = useState(timeValue(session?.checkOutTime));
  const [remarks, setRemarks] = useState(session?.remarks || "");
  const add = useAddSession();
  const edit = useEditSession();
  const busy = add.isPending || edit.isPending;
  const { errors, setErrors, onError } = useErrors(onClose);

  const submit = (e) => {
    e.preventDefault();
    if (!checkIn) return setErrors(["Enter the check-in time."]);
    if (checkOut && checkOut <= checkIn)
      return setErrors(["Check-out must be after check-in."]);
    setErrors([]);
    const body = {
      checkInTime: toManilaIso(day.date, checkIn),
      checkOutTime: checkOut ? toManilaIso(day.date, checkOut) : null,
      remarks: remarks.trim() || undefined,
    };
    const onSuccess = () => {
      toast.success(
        isEdit ? "Check-in period updated." : "Check-in period added.",
      );
      onClose();
    };
    if (isEdit)
      edit.mutate({ sessionId: session.id, ...body }, { onSuccess, onError });
    else
      add.mutate(
        {
          attendanceId: day.id,
          ...body,
          checkOutTime: body.checkOutTime || undefined,
        },
        { onSuccess, onError },
      );
  };

  return (
    <Dialog
      title={isEdit ? "Edit check-in period" : "Add check-in period"}
      subtitle={`${day.employee?.name || employeeName(day.employee)} · ${formatPlainDate(day.date)}`}
      onClose={onClose}
      busy={busy}
      footer={
        <Footer
          onClose={onClose}
          busy={busy}
          label={isEdit ? "Save" : "Add"}
          formId="session-form"
        />
      }
    >
      <form id="session-form" onSubmit={submit} noValidate>
        <Errors errors={errors} />
        <TimeFields
          checkIn={checkIn}
          checkOut={checkOut}
          setCheckIn={setCheckIn}
          setCheckOut={setCheckOut}
        />
        <RemarksField
          value={remarks}
          onChange={setRemarks}
          placeholder="e.g. Forgot to check out; confirmed by supervisor"
        />
        <p className="fs-075 text-slate-500 mt-2 mb-0">
          The change is marked "edited by HR" and kept in the audit log.
        </p>
      </form>
    </Dialog>
  );
};

/** Set a day's status / remarks (ON_LEAVE and HALF_DAY stick; others are recalculated). */
export const DayStatusDialog = ({ day, onClose }) => {
  const [status, setStatus] = useState(day.status || "");
  const [remarks, setRemarks] = useState(day.remarks || "");
  const update = useUpdateAttendanceDay();
  const { errors, onError } = useErrors(onClose);

  const submit = (e) => {
    e.preventDefault();
    update.mutate(
      {
        id: day.id,
        status: status || undefined,
        remarks: remarks.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Day updated.");
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title="Set status / remarks"
      subtitle={`${day.employee?.name || ""} · ${formatPlainDate(day.date)}`}
      onClose={onClose}
      busy={update.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={update.isPending}
          label="Save"
          formId="day-form"
        />
      }
    >
      <form id="day-form" onSubmit={submit} noValidate>
        <Errors errors={errors} />
        <label htmlFor="day-status" className="profile-label">
          Status
        </label>
        <select
          id="day-status"
          className="form-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Keep current</option>
          {DAY_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="fs-070 text-slate-500 mt-1">
          On leave and Half day stay as set; other statuses are recalculated
          from the check-ins.
        </div>
        <RemarksField value={remarks} onChange={setRemarks} />
      </form>
    </Dialog>
  );
};

/** Add a missing day for an employee (HR/manager; deployed employees too, not yourself). */
export const AddDayDialog = ({ onClose }) => {
  const user = useCurrentUser();
  const [employee, setEmployee] = useState(null);
  const [date, setDate] = useState(todayInManila());
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const create = useCreateAttendanceDay();
  const { errors, setErrors, onError } = useErrors(onClose);
  const isSelf = !!employee && employee.id === user.employeeId;

  const submit = (e) => {
    e.preventDefault();
    const list = [];
    if (!employee) list.push("Choose the employee.");
    if (!date) list.push("Pick the date.");
    if (!checkIn && !status)
      list.push("Enter a check-in time or choose a status (e.g. On leave).");
    if (checkIn && checkOut && checkOut <= checkIn)
      list.push("Check-out must be after check-in.");
    setErrors(list);
    if (list.length) return;
    create.mutate(
      {
        employeeId: employee.id,
        date,
        checkInTime: checkIn ? toManilaIso(date, checkIn) : undefined,
        checkOutTime: checkOut ? toManilaIso(date, checkOut) : undefined,
        status: status || undefined,
        remarks: remarks.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(`Attendance added for ${employeeName(employee)}.`);
          onClose();
        },
        onError: (err) =>
          getApiErrorStatus(err) === 403 || getApiErrorStatus(err) === 409
            ? setErrors([getApiErrorMessage(err, "Not allowed.")])
            : onError(err),
      },
    );
  };

  return (
    <Dialog
      title="Add missing day"
      onClose={onClose}
      busy={create.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={create.isPending}
          label="Add day"
          formId="add-day-form"
          disabled={isSelf}
        />
      }
    >
      <form id="add-day-form" onSubmit={submit} noValidate>
        <Errors errors={errors} />
        <label htmlFor="add-day-employee" className="profile-label">
          Employee <span className="text-danger">*</span>
        </label>
        <EmployeePicker
          id="add-day-employee"
          companyId={user.companyId}
          value={employee}
          onChange={setEmployee}
          autoFocus
        />
        {isSelf && (
          <div className="text-danger fs-075 mt-1">
            You cannot edit your own attendance.
          </div>
        )}
        <div className="row g-3 mt-1">
          <div className="col-6">
            <label htmlFor="add-day-date" className="profile-label">
              Date <span className="text-danger">*</span>
            </label>
            <input
              id="add-day-date"
              type="date"
              className="form-control"
              value={date}
              max={todayInManila()}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="col-6">
            <label htmlFor="add-day-status" className="profile-label">
              Status
            </label>
            <select
              id="add-day-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">From the times</option>
              {DAY_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <TimeFields
            checkIn={checkIn}
            checkOut={checkOut}
            setCheckIn={setCheckIn}
            setCheckOut={setCheckOut}
          />
        </div>
        <RemarksField
          value={remarks}
          onChange={setRemarks}
          placeholder="e.g. Biometric device was down"
        />
      </form>
    </Dialog>
  );
};
